import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ChevronRight } from 'lucide-react';
import { ChangeEvent, KeyboardEvent, useCallback, useContext, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useOutside from 'hooks/common/useOutside';
import { OutsideHookTypes } from 'constants/index';
import debounce from 'utils/helpers/debounce';
import useProductSearch from 'hooks/api/useProductSearch';
import { trackEvent } from 'services/userEvent.service';
import Loader from 'components/common/Loader';
import { useTranslation } from 'next-i18next';

import styles from './index.module.scss';
import Item from './components/Item';
import { AppContext } from '../../../contexts';

const Search = ({ search, setSearch }: { search: boolean, setSearch: (params: boolean) => void }) => {
  const router = useRouter();
  const searchRef = useRef(null);
  const inpRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const [value, setValue] = useState('');
  const debounceFunction = useRef<(e: string) => void>(debounce((v: string) => {
    setTerm(v);
    if (v.trim()) trackEvent('search', v.trim());
  }, 750));
  useOutside({
    ref: searchRef,
    wiretapState: search,
    type: OutsideHookTypes.SEARCH,
    func: () => {
      setSearch(false);
      setTerm('');
      setValue('');
    },
  });
  const { currency } = useContext(AppContext);
  const { data: { products }, isLoading } = useProductSearch(term, currency, { enabled: !!term, retry: 0 });
  const onChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
    if (debounceFunction.current) debounceFunction.current(target.value);
    setValue(target.value);
  }, [debounceFunction, setValue]);
  const { t } = useTranslation('header');

  const handleClose = useCallback(() => {
    setSearch(false);
    setTerm('');
    setValue('');
  }, [setSearch, setTerm]);

  const productsRender = useCallback((item: Product.Item, idx: number) => (
    <Item
      {...item}
      href={`/product/${item.link}`}
      handleClose={handleClose}
      key={idx.toString()}
    />
  ), [handleClose]);

  const goToSearchPage = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    trackEvent('search', trimmed);
    setSearch(false);
    setTerm('');
    setValue('');
    void router.push(`/search?q=${encodeURIComponent(trimmed)}`);
  }, [value, setSearch, router]);

  const handleSearch = useCallback(() => {
    if (search && value.trim()) {
      goToSearchPage();
      return;
    }
    setSearch(true);
    if (inpRef.current) inpRef.current.focus();
  }, [setSearch, search, value, goToSearchPage]);

  const onInputKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      goToSearchPage();
    }
  }, [goToSearchPage]);

  return (
    <div className={classNames(styles.searchContainer, { [styles.searchOpen]: search })} ref={searchRef}>
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        onClick={handleSearch}
        className={styles.searchBtn}
      />
      <input
        ref={inpRef}
        type='text'
        name=''
        id=''
        value={value}
        placeholder={t('search.find')}
        onChange={onChange}
        onKeyDown={onInputKeyDown}
        className={classNames({ [styles.inpHide]: !search, [styles.inpShow]: search })}
      />
      <FontAwesomeIcon
        icon={faXmark}
        onClick={handleClose}
        className={classNames({ [styles.xmarkHide]: !search, [styles.xmarkShow]: search })}
      />
      {search && term && !isLoading
        ? (
          <div className={styles.cardContainer}>
            {products.length ? (
              <span className={styles.resultsCount}>{t('search.found', { count: products.length })}</span>
            ) : null}
            {
              products.length
                ? products?.map(productsRender)
                : <span className={styles.empty}>{t('search.noCardsMsg')}</span>
            }
            {products.length
              ? (
                <button
                  type='button'
                  onClick={goToSearchPage}
                  className={styles.allResultsBtn}
                >
                  {t('search.allResults')}
                  <ChevronRight size={14} />
                </button>
              )
              : null}
          </div>
        )
        : null}
      {
        search && isLoading ? (
          <div className={classNames(styles.cardContainer, styles.cardContainerLoader)}>
            <Loader />
          </div>
        ) : null
      }
    </div>
  );
};

export default Search;
